/**
 * OTRIX Play — build a downloadable .ics from the weekly routine so the client can add
 * it to Google / Apple Calendar. One weekly-recurring event per day (07:00, 1h) whose
 * description lists that day's exercises.
 */

const BYDAY = { 1: 'MO', 2: 'TU', 3: 'WE', 4: 'TH', 5: 'FR', 6: 'SA', 7: 'SU' };

const pad = (n) => String(n).padStart(2, '0');

const fmt = (d) =>
  `${d.getFullYear()}${pad(d.getMonth() + 1)}${pad(d.getDate())}T${pad(d.getHours())}${pad(d.getMinutes())}00`;

/**
 * Próxima ocurrencia de un día ISO (1=Lun..7=Dom) a las 07:00 local — hoy mismo si coincide.
 *
 * Es el puente "día de la rutina → fecha real" y lo comparten el .ics, el deep-link de Google y
 * el salto a la Agenda. Ojo: la Agenda arranca la semana en DOMINGO, así que traducir por
 * "domingo de esta semana" caería en el pasado; "próxima ocurrencia" es lo que la gente espera.
 */
export const nextWeekday = (isoDay) => {
  const jsTarget = isoDay === 7 ? 0 : isoDay; // JS: 0=Sun..6=Sat
  const d = new Date();
  d.setHours(7, 0, 0, 0);
  const diff = (jsTarget - d.getDay() + 7) % 7;
  d.setDate(d.getDate() + diff);
  return d;
};

const escapeText = (s) => (s || '').replace(/([,;\\])/g, '\\$1').replace(/\n/g, '\\n');

export const buildRoutineIcs = (routine, dayLabelFn) => {
  const items = routine?.items || [];
  const byDay = {};
  items.forEach((it) => {
    (byDay[it.day_of_week] = byDay[it.day_of_week] || []).push(it);
  });

  const lines = ['BEGIN:VCALENDAR', 'VERSION:2.0', 'PRODID:-//OTRIX//OTRIX Play//ES', 'CALSCALE:GREGORIAN'];

  Object.keys(byDay).forEach((day) => {
    const d = nextWeekday(Number(day));
    const end = new Date(d.getTime() + 60 * 60 * 1000);
    const names = byDay[day].map((i) => i.exercise?.localized_name).filter(Boolean).join(', ');

    lines.push(
      'BEGIN:VEVENT',
      `UID:otrix-play-${day}-${Date.now()}@otrixapp.com`,
      `DTSTART:${fmt(d)}`,
      `DTEND:${fmt(end)}`,
      `RRULE:FREQ=WEEKLY;BYDAY=${BYDAY[day]}`,
      `SUMMARY:${escapeText(`OTRIX Play - ${dayLabelFn(Number(day))}`)}`,
      `DESCRIPTION:${escapeText(names)}`,
      'END:VEVENT'
    );
  });

  lines.push('END:VCALENDAR');
  return lines.join('\r\n');
};

/**
 * Deep-link de Google Calendar para AGREGAR directamente el evento recurrente de un día
 * (sin subir/descargar .ics). `render?action=TEMPLATE` crea UN evento por URL, así que un día
 * = una URL (el llamador abre una por día de entreno).
 */
export const buildGoogleCalendarUrl = (dayItems, day, dayLabelFn) => {
  const d = nextWeekday(Number(day));
  const end = new Date(d.getTime() + 60 * 60 * 1000);
  const names = (dayItems || []).map((i) => i.exercise?.localized_name).filter(Boolean).join(', ');
  const params = new URLSearchParams({
    action: 'TEMPLATE',
    text: `OTRIX Play - ${dayLabelFn(Number(day))}`,
    dates: `${fmt(d)}/${fmt(end)}`,
    details: names,
    recur: `RRULE:FREQ=WEEKLY;BYDAY=${BYDAY[day]}`
  });
  return `https://calendar.google.com/calendar/render?${params.toString()}`;
};

/** Una URL de Google Calendar por cada día de la rutina que tenga ejercicios. */
export const buildGoogleCalendarUrls = (routine, dayLabelFn) => {
  const items = routine?.items || [];
  const byDay = {};
  items.forEach((it) => {
    (byDay[it.day_of_week] = byDay[it.day_of_week] || []).push(it);
  });
  return Object.keys(byDay)
    .sort((a, b) => Number(a) - Number(b))
    .map((day) => buildGoogleCalendarUrl(byDay[day], Number(day), dayLabelFn));
};

export const downloadIcs = (content, filename = 'otrix-play-rutina.ics') => {
  const blob = new Blob([content], { type: 'text/calendar;charset=utf-8' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
};
